import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { APIClinicBilling } from 'lib/api/firebase/apis/api-clinic-balance';
import { AssessmentType } from 'lib/documents/patient-assessment/patient-assessment';
import { queryKeyFactory } from 'lib/utils/queryKeyFactory';
import { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useClinicianContext } from '../use-resolveCurrentClinicInfo';

interface ProductArgs {
  type: AssessmentType;
  productType: 'bulk' | 'on-demand';
  quantity: number;
}

export function useGetProductPrice(
  product: ProductArgs,
  options?: Pick<UseQueryOptions, 'enabled'>
) {
  const clinicContext = useClinicianContext();
  return useQuery({
    ...options,
    queryKey: ['product', product.type, 'qty', product.quantity],
    queryFn: () =>
      APIClinicBilling.getProductPrice({
        clinicId: clinicContext.currentClinic,
        type: product.type,
        productType: product.productType,
        units: product.quantity
      })
  });
}

export function useGetProductPriceDebounced(
  product: ProductArgs,
  options?: Pick<UseQueryOptions, 'enabled'>,
  debounceTime?: number
) {
  const [productArgs, setProductArgs] = useState<ProductArgs>(product);
  const debouncedProductArgs = useMemo(
    () => debounce((product: ProductArgs) => setProductArgs(product), debounceTime ?? 500),
    [setProductArgs, debounceTime]
  );

  useEffect(() => {
    debouncedProductArgs(product)
  }, [product, debouncedProductArgs])

  return useGetProductPrice(productArgs, options);
}

export function useHasPaymentMethod(clinicId: string) {
  return useQuery({
    queryKey: queryKeyFactory.clinic.billing.hasPaymentMethod(clinicId),
    queryFn: () => APIClinicBilling.hasPaymentMethod({ clinicId }),
    enabled: clinicId !== ''
  });
}

export function useCurrentClinicHasPaymentMethod() {
  const clinicianContext = useClinicianContext();

  /**
   * Check if the organisation has a payment method set up
   */
  const hasPaymentMethodQuery = useHasPaymentMethod(
    clinicianContext.currentClinic
  );

  return hasPaymentMethodQuery;
}
